import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";
import { diasem2, fecha, ymd } from "./calendario";

const SHEET = "FE Facturas";
const TABLE = "FacturasFE";

const rnd = seededRandom(1101);
// Folio, Emisión, Días de crédito
const ROWS = Array.from({ length: 30 }, (_, i) => [
  `F-${8801 + i}`,
  fecha(2025, 7, 1) + rnd.int(0, 540),
  rnd.pick([15, 30, 30, 45, 60]),
] as [string, number, number]).sort((a, b) => a[1] - b[1]);

type Row = (typeof ROWS)[number];
const venc = (r: Row) => r[1] + r[2];
const pagoHabil = (r: Row) => {
  const v = venc(r);
  return v + (diasem2(v) === 6 ? 2 : diasem2(v) === 7 ? 1 : 0);
};

export const spec: FuncionProjectSpec = {
  projectId: "funciones-componentes-fecha",
  sheets: [
    {
      name: SHEET,
      title: "Calendario de facturas",
      subtitle: "Facturas emitidas entre 2025 y 2026 — descompón y construye fechas con fórmulas",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Folio", "Emisión", "Días de crédito"],
          rows: ROWS,
          answerColumns: ["Año", "Mes", "Día semana", "Inicio de mes", "Trimestre", "Pago hábil", "Renovación"],
          formats: {
            Emisión: "dd/mm/yyyy",
            "Inicio de mes": "dd/mm/yyyy",
            "Pago hábil": "ddd dd/mm/yyyy",
            Renovación: "dd/mm/yyyy",
          },
        },
      ],
    },
  ],
  checks: {
    1: { kind: "column", sheet: SHEET, table: TABLE, column: "Año", fns: ["YEAR"], expected: ROWS.map((r) => ymd(r[1]).y) },
    2: { kind: "column", sheet: SHEET, table: TABLE, column: "Mes", fns: ["MONTH"], expected: ROWS.map((r) => ymd(r[1]).m) },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Día semana", fns: ["WEEKDAY"],
      expected: ROWS.map((r) => diasem2(r[1])),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Inicio de mes", fns: ["DATE", "YEAR", "MONTH"],
      expected: ROWS.map((r) => fecha(ymd(r[1]).y, ymd(r[1]).m, 1)),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Trimestre", fns: ["MONTH", "ROUNDUP"], exact: true,
      expected: ROWS.map((r) => `T${Math.ceil(ymd(r[1]).m / 3)}`),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Pago hábil", fns: ["IF", "WEEKDAY"],
      expected: ROWS.map(pagoHabil),
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Renovación", fns: ["DATE", "YEAR", "MONTH", "DAY"],
      expected: ROWS.map((r) => {
        const { y, m, d } = ymd(r[1]);
        return fecha(y + 1, m, d);
      }),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-componentes-fecha",
  title: "FECHA, AÑO, MES, DIA y DIASEM: Calendario de facturas",
  subtitle: "Desarmar y armar fechas",
  description:
    "Para Excel, una fecha es un número: el 1 de enero de 2026 es el 46023. Por eso se pueden sumar días, comparar y restar.\n\nEn este proyecto llevas el calendario de cobranza de 30 facturas: sacarás el año, el mes y el día de la semana, armarás fechas nuevas con FECHA y moverás los pagos que caen en fin de semana al lunes.",
  topics: [
    "Una fecha es un número de serie",
    "AÑO, MES, DIA y DIASEM",
    "Construir fechas con FECHA",
    "Trimestre con REDONDEAR.MAS",
    "Mover un vencimiento al siguiente día hábil",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Año",
      description: "En la hoja <strong>FE Facturas</strong>, columna <strong>Año</strong>: obtén el año de la fecha de <strong>Emisión</strong>.",
      solution: [`Selecciona ${F("D5")}.`, `Escribe ${F("=AÑO([@Emisión])")}.`],
      expectedResult: "2025 o 2026",
      finalFormula: "=AÑO([@Emisión])",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Mes",
      description: "En la columna <strong>Mes</strong>: obtén el número de mes (1 a 12) de la Emisión.",
      solution: [`Selecciona ${F("E5")}.`, `Escribe ${F("=MES([@Emisión])")}.`],
      expectedResult: "1 a 12",
      finalFormula: "=MES([@Emisión])",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Día de la semana",
      description:
        "En la columna <strong>Día semana</strong>: devuelve el día de la semana de la Emisión con <strong>lunes = 1</strong> y <strong>domingo = 7</strong>.",
      solution: [
        `Selecciona ${F("F5")}.`,
        `DIASEM tiene un segundo argumento, tipo. Con ${F("2")} la semana empieza en lunes (1) y termina en domingo (7). Sin él, empieza en domingo.`,
        `Escribe ${F("=DIASEM([@Emisión],2)")}.`,
      ],
      expectedResult: "1 (lunes) a 7 (domingo)",
      finalFormula: "=DIASEM([@Emisión],2)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Primer día del mes",
      description: "En la columna <strong>Inicio de mes</strong>: devuelve el <strong>día 1</strong> del mes de la Emisión.",
      solution: [
        `Selecciona ${F("G5")}.`,
        "FECHA(año, mes, día) arma una fecha a partir de sus partes. Usa el año y el mes de la emisión y el día 1.",
        `Escribe ${F("=FECHA(AÑO([@Emisión]),MES([@Emisión]),1)")}.`,
      ],
      expectedResult: "01/03/2026 si la emisión fue el 17/03/2026",
      finalFormula: "=FECHA(AÑO([@Emisión]),MES([@Emisión]),1)",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Trimestre",
      description:
        "En la columna <strong>Trimestre</strong>: escribe <strong>T1</strong>, <strong>T2</strong>, <strong>T3</strong> o <strong>T4</strong> según el mes de la Emisión (enero–marzo = T1, …).",
      solution: [
        `Selecciona ${F("H5")}.`,
        `Divide el mes entre 3 y redondea hacia arriba: marzo → 3/3 = 1; abril → 4/3 = 1.33 → 2. ${F("REDONDEAR.MAS(número,0)")} redondea hacia arriba.`,
        `Escribe ${F('="T"&REDONDEAR.MAS(MES([@Emisión])/3,0)')}.`,
      ],
      expectedResult: "T1 a T4",
      finalFormula: '="T"&REDONDEAR.MAS(MES([@Emisión])/3,0)',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Pago en día hábil",
      description: conditions(
        "En la columna <strong>Pago hábil</strong>: el vencimiento es Emisión + Días de crédito. Si cae en fin de semana, el pago se recorre al <strong>lunes</strong>:",
        [
          "Si cae en <strong>sábado</strong> (DIASEM = 6) → suma 2 días",
          "Si cae en <strong>domingo</strong> (DIASEM = 7) → suma 1 día",
          "Si cae entre semana → se queda igual",
        ]
      ),
      solution: [
        `Selecciona ${F("I5")}.`,
        "Calcula el vencimiento y súmale lo que haga falta según el día de la semana.",
        `Escribe ${F("=[@Emisión]+[@[Días de crédito]]+SI(DIASEM([@Emisión]+[@[Días de crédito]],2)=6,2,SI(DIASEM([@Emisión]+[@[Días de crédito]],2)=7,1,0))")}.`,
        "La columna muestra el nombre del día (formato ddd) para que compruebes que ninguno cae en sábado o domingo.",
      ],
      expectedResult: "Vencimientos de lunes a viernes",
      finalFormula:
        "=[@Emisión]+[@[Días de crédito]]+SI(DIASEM([@Emisión]+[@[Días de crédito]],2)=6,2,SI(DIASEM([@Emisión]+[@[Días de crédito]],2)=7,1,0))",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Renovación anual",
      description: "En la columna <strong>Renovación</strong>: la factura se renueva el <strong>mismo día y mes del año siguiente</strong>. Arma esa fecha con FECHA, AÑO, MES y DIA.",
      solution: [
        `Selecciona ${F("J5")}.`,
        "Sumar 365 días falla en años bisiestos. Lo correcto es sumar 1 al año.",
        `Escribe ${F("=FECHA(AÑO([@Emisión])+1,MES([@Emisión]),DIA([@Emisión]))")}.`,
      ],
      expectedResult: "La misma fecha un año después",
      finalFormula: "=FECHA(AÑO([@Emisión])+1,MES([@Emisión]),DIA([@Emisión]))",
    },
  ],
};
